import React, { useEffect, useId, useRef } from 'react';
import { Col, Form, Nav, Row } from 'react-bootstrap';
import './CoordinatorSettings.css';

/**
 * The Settings tab: each setting a coordinator changes now and then, one
 * category at a time. `sections` is `[{ key, title, content }]`; each
 * section's content carries its own heading and its one Save. The category
 * shown is `focusKey` (from `?show=`), or the first one; choosing another
 * calls `onSelect(key)`, which puts it in the URL in place (as a tab choice
 * is), so a link or a bookmark opens that category and a link to a retired
 * tab such as `?tab=decision-emails` opens the category that replaced it.
 * Desktop lists the categories beside the section; a phone chooses them from
 * a select.
 */
function CoordinatorSettings({ sections, focusKey, onSelect }) {
    const selectId = useId();
    const region = useRef(null);
    const linked = sections.some((section) => section.key === focusKey);
    const current = linked ? focusKey : sections[0]?.key;
    const shown = sections.find((section) => section.key === current);

    // A link or a choice that named a category moves focus to it
    useEffect(() => {
        if (linked && region.current) {
            region.current.scrollIntoView({ block: 'start' });
            region.current.focus();
        }
    }, [focusKey, linked]);

    const choose = (key) => {
        if (key && key !== current && onSelect) onSelect(key);
    };

    return (
        <Row className="g-4 coordinator-settings">
            <Col md={4} lg={3}>
                <Form.Group className="d-md-none" controlId={selectId}>
                    <Form.Label>Settings category</Form.Label>
                    <Form.Select
                        value={current}
                        onChange={(event) => choose(event.target.value)}
                    >
                        {sections.map((section) => (
                            <option key={section.key} value={section.key}>
                                {section.title}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <nav
                    aria-label="Settings sections"
                    className="d-none d-md-block coordinator-settings-nav"
                >
                    <Nav variant="pills" className="flex-column gap-1">
                        {sections.map((section) => (
                            <Nav.Item key={section.key}>
                                <Nav.Link
                                    as="button"
                                    type="button"
                                    className="w-100 text-start"
                                    active={section.key === current}
                                    aria-current={
                                        section.key === current
                                            ? 'true'
                                            : undefined
                                    }
                                    onClick={() => choose(section.key)}
                                >
                                    {section.title}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>
                </nav>
            </Col>
            <Col md={8} lg={9}>
                {shown && (
                    <section
                        key={shown.key}
                        aria-label={shown.title}
                        ref={region}
                        tabIndex={-1}
                        className="coordinator-settings-region"
                    >
                        {shown.content}
                    </section>
                )}
            </Col>
        </Row>
    );
}

export default CoordinatorSettings;
